// Раздел «Скаут»: календарь пролётов района, картинка любого снимка, отметки
// судов там, где снимок осмотрен, и — кабинету с признаком `imagery_dataset` —
// сверка с АИС поверх.
//
// До 28.10.2026 это были два режима, «Осмотр» и «Датасет», с одним и тем же
// снимком, одним прибором и двумя копиями карты (план
// `docs/plans/PLAN_2026-10-28-scout-unify.md`). Разделение осталось только по
// ЗРИТЕЛЮ: эталон АИС и суда всего эфира — внутренние, и их слои рисуются лишь
// при признаке; их ручки отказывают без него сами.
//
// Отметка живёт на момент съёмки КОНКРЕТНОЙ сцены, поэтому раздел свой, а не
// строка в каталоге слоёв карты: точка без времени прочиталась бы как «сейчас».
// Рамки осмотра идут вместе с отметками: пустая карта с рамкой значит «искали,
// не нашли», без рамки — «сюда не смотрели».

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { cabinetApi, type ImageryMarksResponse } from '../api'
import { useT } from '../i18n'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { CoordReadout } from '../map/CoordReadout'
import { addUnderBoundaries, boundaryBeforeId } from '../map/layerOrder'
import { useOrg } from '../org'
import { useRegionScope } from '../species/useRegionScope'
import { DatasetMonth } from './DatasetMonth'
import { DayScenes, SETS } from './DayScenes'
import { MarkCard, type MarkProps } from './MarkCard'
import { RecordCard } from './RecordCard'
import {
  listScenes, pickMonth, recordCount, sceneKey, scenesOfSensor, setCount, setItems, stepItem, stepScene,
  type DatasetSet, type SetItem,
} from './datasetCalendar'
import {
  DS_AIS_LAYER, DS_AIS_SOURCE, DS_FOOTPRINT_LAYER, DS_FOOTPRINT_SOURCE, DS_MARKS_LAYER, DS_MARKS_SOURCE,
  DS_INSTRUMENT_DOT_LAYER, DS_INSTRUMENT_LINE_LAYER, DS_INSTRUMENT_SOURCE, instrumentCollection, instrumentPoint,
  DS_PICTURE_LAYER, DS_PICTURE_SOURCE, DS_RECORDS_HALO_LAYER, DS_RECORDS_LAYER, DS_RECORDS_SOURCE, MARK_COLOR,
  aisCollection, cogLayerPlan, cogSourceSpec, footprintBounds, footprintCollection, marksCollection,
  recordsCollection, ringRadiusExpression, statusColor, statusColorExpression, type DatasetScene,
} from './datasetLayer'
import { SCOUT_KEYS, markKey, parseScoutUrl, writeScoutUrl, type ScoutUrlState } from './imageryUrl'
import {
  LOOKED_FILL_LAYER, LOOKED_LINE_LAYER, LOOKED_SOURCE, MARKS_LAYER, MARKS_SOURCE, WAKE_LAYER, WAKE_SOURCE,
  aisStrokeColorExpression, aisStrokeWidthExpression, flattenMark, markFilter, verdictColorExpression,
  verdictFacets,
} from './markLayer'
import { POLL_MS, scanView, type ScanJob } from './scanState'
import { datasetMarkProps, markCountsByScene, sceneOfSweep, sweepsOfScene } from './scoutSweeps'
import { MapPanel } from '../map/MapPanel'
import { useScoutMap } from './useScoutMap'
import { verdictColor, verdictLabelKey } from './verdictStyle'
import { wakeCollection } from './wakeRay'

const EMPTY_FC = { type: 'FeatureCollection', features: [] } as const
// Дальневосточный вид по умолчанию: раздел про наши акватории.
const HOME = { center: [150, 45] as [number, number], zoom: 5 }
/** Окно отметок без выбранного дня — как у прежнего «Осмотра». */
const MARKS_DAYS = 14

export function ImageryPage() {
  const t = useT()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const allowed = useOrg().activeOrg?.services?.imagery_dataset === true

  const [searchParams, setSearchParams] = useSearchParams()
  const [st, setSt] = useState<ScoutUrlState>(() => parseScoutUrl(searchParams))
  const patch = (p: Partial<ScoutUrlState>) => setSt(prev => ({ ...prev, ...p }))
  const aisOn = allowed && st.ais

  useEffect(() => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      for (const k of SCOUT_KEYS) next.delete(k)
      writeScoutUrl(st).forEach((v, k) => next.set(k, v))
      return next
    }, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st])

  // Яркость и цветной TCI — настройки ЧТЕНИЯ снимка, в ссылку не идут.
  const [opacity, setOpacity] = useState(1)
  const [visual, setVisual] = useState(false)
  // Снимки без записей (суша, лёд, вода без АИС) при сверке скрыты, пока их не попросили.
  const [allScenes, setAllScenes] = useState(false)

  // Экземпляр карты для читалки координат: она подписывается на курсор сама.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const { mapRef, gateRef, box, boundaryMeta } = useScoutMap({
    containerRef, home: HOME,
    boundaries: { eez: st.eez, districts: st.fd },
    // Рубежи над снимком, но под отметками.
    boundaryBefore: MARKS_LAYER,
    onLoad: map => {
      setMapInstance(map)
      addScoutLayers(map, {
        onSweepMark: key => setSt(prev => ({ ...prev, selected: key, rec: null, mark: null })),
        onRecord: id => setSt(prev => ({ ...prev, rec: id, mark: null, selected: null })),
        onDsMark: id => setSt(prev => ({ ...prev, mark: id, rec: null, selected: null })),
      })
    },
  })

  const { regions } = useRegionScope()

  // ---- Календарь пролётов. Со сверкой — тот же календарь с записями. ----
  const askMonth = st.month ?? new Date().toISOString().slice(0, 7)
  const calendarQuery = useQuery({
    queryKey: ['imagery', aisOn ? 'dataset' : 'passes', 'calendar', st.region, askMonth],
    queryFn: () => (aisOn
      ? cabinetApi.imagery.dataset.calendar(st.region, askMonth)
      : cabinetApi.imagery.passes.calendar(st.region, askMonth)),
    placeholderData: prev => prev,
  })
  const months = calendarQuery.data?.months ?? []
  // Без месяца в ссылке открываем последний с пролётами.
  useEffect(() => {
    if (st.month == null && calendarQuery.data) {
      const m = pickMonth(months, null)
      if (m && m !== askMonth) patch({ month: m })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarQuery.data])

  // ---- Снимки дня: пролёты всем; записи и отметки датасета — со сверкой. ----
  const passDayQuery = useQuery({
    queryKey: ['imagery', 'passes', 'day', st.region, st.day],
    queryFn: () => cabinetApi.imagery.passes.day(st.region, st.day!),
    enabled: st.day != null && !aisOn,
  })
  const dsDayQuery = useQuery({
    queryKey: ['imagery', 'dataset', 'day', st.region, st.day],
    queryFn: () => cabinetApi.imagery.dataset.day(st.region, st.day!),
    enabled: st.day != null && aisOn,
  })
  const dsDay = aisOn && st.day ? dsDayQuery.data : undefined
  const dayScenesRaw: DatasetScene[] = (aisOn ? dsDay?.scenes : passDayQuery.data?.scenes) ?? []
  const dayError = aisOn ? dsDayQuery.isError : passDayQuery.isError

  // ---- Отметки осмотров кабинета: за сутки выбранного дня. ----
  const marksQuery = useQuery({
    queryKey: ['imagery', 'marks', box, st.day],
    queryFn: () => cabinetApi.imagery.marks(box!, MARKS_DAYS, st.day),
    enabled: box != null,
    staleTime: 60_000,
    // Рамка стоит в ключе: без отстоявшихся данных раздел на каждом сдвиге
    // карты становился бы пустым, а слой картинки ронял бы кэш плиток.
    placeholderData: prev => prev,
  })
  const data = marksQuery.data as ImageryMarksResponse | undefined
  const looked = useMemo(() => data?.looked.features ?? [], [data])

  // Старая ссылка «Осмотра» `pic=<номер осмотра>` → день и снимок этого осмотра.
  const legacyQuery = useQuery({
    queryKey: ['imagery', 'marks', 'legacy', box, st.legacyPic],
    queryFn: () => cabinetApi.imagery.marks(box!, 365),
    enabled: box != null && st.legacyPic != null,
  })
  useEffect(() => {
    if (st.legacyPic == null || !legacyQuery.data) return
    const hit = sceneOfSweep(legacyQuery.data.looked.features, st.legacyPic)
    patch(hit ? { day: hit.day, month: hit.day.slice(0, 7), scene: hit.scene, legacyPic: null }
      : { legacyPic: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyQuery.data])

  const markCounts = useMemo(() => markCountsByScene(looked, data?.marks.features ?? []), [looked, data])

  const sensorScenes = useMemo(() => scenesOfSensor(dayScenesRaw, st.sensor), [dayScenesRaw, st.sensor])
  const emptyScenes = useMemo(() => sensorScenes.filter(s => recordCount(s) === 0).length, [sensorScenes])
  const scenes = useMemo(() => listScenes(sensorScenes, aisOn
    ? { sensor: 'all', set: st.set, showEmpty: allScenes, keep: st.scene }
    : { sensor: 'all', set: 'all', showEmpty: true, keep: st.scene }),
  [sensorScenes, aisOn, st.set, allScenes, st.scene])
  const setTotals = useMemo(() => Object.fromEntries(SETS.map(k => [
    k, sensorScenes.reduce((a, s) => a + setCount(s, k), 0),
  ])) as Record<DatasetSet, number>, [sensorScenes])
  const scene = useMemo(() => scenes.find(s => sceneKey(s) === st.scene) ?? null, [scenes, st.scene])
  const sceneSweeps = useMemo(() => (scene ? sweepsOfScene(looked, sceneKey(scene)) : null),
                              [looked, scene])

  // Отметки осмотров: выбран снимок — только его осмотров; иначе все осмотры дня.
  const sweepMarks = useMemo(() => {
    if (!data) return EMPTY_FC
    return {
      type: 'FeatureCollection' as const,
      features: data.marks.features
        .filter(f => !sceneSweeps || sceneSweeps.has(Number(f.properties.sweep_id)))
        .map(f => ({ ...f, properties: flattenMark(f.properties as Record<string, unknown>) })),
    }
  }, [data, sceneSweeps])
  const lookedFc = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: looked.filter(f => !sceneSweeps || sceneSweeps.has(Number(f.properties?.sweep_id))),
  }), [looked, sceneSweeps])
  const searchedKm2 = lookedFc.features.reduce((a, f) => a + Number(f.properties?.sea_km2 ?? 0), 0)
  const wakes = useMemo(() => wakeCollection(sweepMarks.features.map(f => {
    const c = (f.geometry as { coordinates: [number, number] }).coordinates
    const p = f.properties as Record<string, unknown>
    return {
      lon: c[0], lat: c[1],
      course_deg: (p.course_deg as number | null) ?? null,
      evidence: (p.evidence as Record<string, number | boolean | null>) ?? null,
    }
  })), [sweepMarks])

  // ---- Сверка с АИС. ----
  const sceneKeyOrNull = scene ? sceneKey(scene) : null
  const records = useMemo(
    () => (aisOn ? recordsCollection(dsDay?.records ?? [], sceneKeyOrNull, st.set) : EMPTY_FC),
    [aisOn, dsDay, sceneKeyOrNull, st.set])
  const dayMarks = useMemo(() => dsDay?.marks ?? [], [dsDay])
  const dsMarks = useMemo(() => (aisOn ? marksCollection(dayMarks, sceneKeyOrNull, st.set) : EMPTY_FC),
                          [aisOn, dayMarks, sceneKeyOrNull, st.set])
  const ais = useMemo(() => (aisOn ? aisCollection(dsDay?.ais ?? []) : EMPTY_FC), [aisOn, dsDay])
  const items = useMemo(() => (aisOn ? setItems(scenes, dsDay?.records ?? [], dayMarks, st.set) : []),
                        [aisOn, scenes, dsDay, dayMarks, st.set])
  const current = st.rec != null ? { kind: 'record', id: st.rec }
    : st.mark != null ? { kind: 'mark', id: st.mark } : null
  const itemIndex = current ? items.findIndex(x => x.kind === current.kind && x.id === current.id) : -1
  const position = aisOn && st.set !== 'all' ? { i: itemIndex + 1, n: items.length } : null
  const selectedRecord = aisOn ? (dsDay?.records ?? []).find(r => r.record_id === st.rec) ?? null : null
  // Та же запись, что у карточки (общий кэш): точка прибора рядом с позицией АИС.
  const recordQuery = useQuery({
    queryKey: ['imagery', 'dataset', 'record', selectedRecord?.record_id],
    queryFn: () => cabinetApi.imagery.dataset.record(selectedRecord!.record_id),
    enabled: selectedRecord != null,
  })
  const instrument = useMemo(
    () => (selectedRecord && recordQuery.data
      ? instrumentCollection(selectedRecord, instrumentPoint(recordQuery.data.attempts))
      : instrumentCollection({ lat: 0, lon: 0 }, null)),
    [selectedRecord, recordQuery.data])
  const selectedDsMark = aisOn ? dayMarks.find(m => m.mark_id === st.mark) ?? null : null

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      const set = (id: string, fc: unknown) =>
        (map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(fc as never)
      set(MARKS_SOURCE, sweepMarks)
      set(WAKE_SOURCE, wakes)
      set(LOOKED_SOURCE, st.looked ? lookedFc : EMPTY_FC)
      set(DS_AIS_SOURCE, ais)
      set(DS_RECORDS_SOURCE, records)
      set(DS_MARKS_SOURCE, dsMarks)
      set(DS_FOOTPRINT_SOURCE, footprintCollection(scene))
      set(DS_INSTRUMENT_SOURCE, instrument)
    })
  }, [sweepMarks, wakes, lookedFc, st.looked, ais, records, dsMarks, scene, instrument, mapRef, gateRef])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      if (map.getLayer(MARKS_LAYER)) map.setFilter(MARKS_LAYER, markFilter(st.verdicts, []) as never)
    })
  }, [st.verdicts, mapRef, gateRef])

  // ---- Картинка снимка: тайлы COG на лету. Слой перестраивается по АДРЕСУ
  // тайлов (`cogLayerPlan`), а не по объекту снимка: свежий ответ приносит
  // новый объект с теми же значениями, и кэш плиток падал бы зря. ----
  const channel = scene?.sensor === 'optical' && visual ? 'visual' : 'nir'
  const tileScene = scene ? { source: scene.source, scene_id: scene.scene_id } : null
  const pictureUrlRef = useRef<string | null>(null)
  const planUrl = cogLayerPlan(null, tileScene, channel).url
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    const plan = cogLayerPlan(pictureUrlRef.current, tileScene, channel)
    if (plan.action === 'keep') return
    gateRef.current.run(() => {
      if (map.getLayer(DS_PICTURE_LAYER)) map.removeLayer(DS_PICTURE_LAYER)
      if (map.getSource(DS_PICTURE_SOURCE)) map.removeSource(DS_PICTURE_SOURCE)
      pictureUrlRef.current = plan.url
      if (!tileScene) return
      map.addSource(DS_PICTURE_SOURCE, cogSourceSpec(tileScene, channel) as never)
      // САМЫМ НИЖНИМ из оверлеев: точки, рамки и рубежи читаются поверх пикселей.
      map.addLayer(
        { id: DS_PICTURE_LAYER, type: 'raster', source: DS_PICTURE_SOURCE,
          paint: { 'raster-opacity': opacity } },
        map.getLayer(DS_AIS_LAYER)
          ? DS_AIS_LAYER
          : boundaryBeforeId((map.getStyle().layers ?? []).map(l => l.id)),
      )
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planUrl])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      if (map.getLayer(DS_PICTURE_LAYER)) map.setPaintProperty(DS_PICTURE_LAYER, 'raster-opacity', opacity)
    })
  }, [opacity, mapRef, gateRef])

  // ---- «Осмотреть снимок». Прогон живёт в базе, а не во вкладке: ход
  // спрашивается и после перезагрузки страницы. ----
  const qc = useQueryClient()
  const scanQuery = useQuery({
    queryKey: ['imagery', 'scan'],
    queryFn: () => cabinetApi.imagery.scanStatus(),
    refetchInterval: q => {
      const job = (q.state.data as { job: ScanJob | null } | undefined)?.job
      return job && (job.status === 'queued' || job.status === 'running') ? POLL_MS : false
    },
  })
  const job = (scanQuery.data as { job: ScanJob | null } | undefined)?.job ?? null
  const scan = useMemo(() => scanView(job), [job])
  // Отметки уезжают в базу пачками по ходу осмотра — карта их подхватывает.
  useEffect(() => {
    if (!job) return
    qc.invalidateQueries({ queryKey: ['imagery', 'marks'] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.marks, job?.scenes_done, job?.status])
  const scanStart = useMutation({
    mutationFn: (key: string) => cabinetApi.imagery.scan(box!, MARKS_DAYS, key),
    onSuccess: () => { void scanQuery.refetch() },
  })

  /** Выбор снимка явным действием — карта едет к его контуру. */
  const pickScene = (key: string | null) => {
    patch({ scene: key, rec: null, mark: null, selected: null })
    const s = dayScenesRaw.find(x => sceneKey(x) === key)
    const map = mapRef.current
    const bounds = footprintBounds(s?.footprint ? flatCoords(s.footprint.coordinates) : [])
    if (map && bounds) map.fitBounds(bounds, { padding: 60, maxZoom: 9, duration: 600 })
  }
  /** Переход к элементу набора сверки: его снимок, его карточка, карта — к нему. */
  const jumpTo = (item: SetItem | null) => {
    if (!item) return
    patch({
      scene: item.scene, selected: null,
      rec: item.kind === 'record' ? item.id : null,
      mark: item.kind === 'mark' ? item.id : null,
    })
    const map = mapRef.current
    if (map) map.easeTo({ center: [item.lng, item.lat], zoom: Math.max(map.getZoom(), 11), duration: 500 })
  }
  const step = (d: number) => (position
    ? jumpTo(stepItem(items, current, d))
    : pickScene(stepScene(scenes, st.scene, d)))
  const chooseSet = (set: DatasetSet) => patch({ set, rec: null, mark: null })

  // Клавиши сверки: 1/2/3 — набор, [ ] — шаг. Слушатель один и читает свежее
  // состояние через ref.
  const keysRef = useRef({ aisOn, chooseSet, step })
  keysRef.current = { aisOn, chooseSet, step }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!keysRef.current.aisOn || e.ctrlKey || e.metaKey || e.altKey) return
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return
      const i = ['1', '2', '3'].indexOf(e.key)
      if (i >= 0) keysRef.current.chooseSet(SETS[i])
      else if (e.key === '[') keysRef.current.step(-1)
      else if (e.key === ']') keysRef.current.step(1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const selectedSweepMark = useMemo(() => {
    if (!st.selected || !data) return null
    const [sweep, local] = st.selected.split(':').map(Number)
    const f = data.marks.features.find(
      x => Number(x.properties.sweep_id) === sweep && Number(x.properties.local_id) === local)
    if (!f) return null
    // Время съёмки и сцена — от осмотра: без них точка читается как «сейчас».
    const sw = looked.find(x => Number(x.properties?.sweep_id) === sweep)?.properties
    const at = (f.geometry as { coordinates: [number, number] }).coordinates
    return {
      ...(f.properties as unknown as MarkProps),
      lon: at[0], lat: at[1],
      sensor: (sw?.sensor as string) ?? undefined,
      captured_at: (sw?.captured_at as string) ?? undefined,
      scene_id: (sw?.scene_id as string) ?? undefined,
      source: (sw?.source as string) ?? undefined,
      thresholds: data.thresholds ?? null,
    } as MarkProps
  }, [st.selected, data, looked])
  const dsMarkCard = useMemo(() => {
    if (!selectedDsMark) return null
    const sc = dayScenesRaw.find(s => sceneKey(s) === sceneKey(selectedDsMark)) ?? null
    return datasetMarkProps(selectedDsMark, sc, (data?.thresholds ?? null) as MarkProps['thresholds'])
  }, [selectedDsMark, dayScenesRaw, data])

  const calendarMonth = st.month ?? pickMonth(months, null) ?? askMonth
  const calendarFailed = calendarQuery.isError
  const facets = verdictFacets(sweepMarks.features as { properties: { verdict?: unknown } }[])
  const inspected = scene != null && (sceneSweeps?.size ?? 0) > 0
  const scanBusy = scan.busy
  const scanOfScene = job?.scene_key != null && job.scene_key === sceneKeyOrNull

  return (
    <div className="relative h-full w-full">
      {/* Высота, а НЕ `absolute inset-0` — сторож `map/mapContainer.test.ts`. */}
      <div ref={containerRef} className="h-full w-full" />
      <BasemapAttribution corner="bl" />
      <CoordReadout map={mapInstance} />

      <MapPanel storageKey="imagery.panel" className="w-[296px]" title={t('nav.imagery')} meta={sweepMarks.features.length}>
        <div className="space-y-2 px-3 pb-3 text-[12px]">

          {regions.length > 1 && (
            <select value={st.region}
                    onChange={e => patch({ region: e.target.value, month: null, day: null, scene: null, rec: null, mark: null })}
                    aria-label={t('imagery.ds.region')}
                    className="w-full rounded-lg border border-hull bg-trench px-2 py-1 text-[12px] text-foam">
              {regions.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
            </select>
          )}

          {allowed && (
            <label className="flex items-center gap-2 text-[11px] text-mist">
              <input type="checkbox" checked={st.ais}
                     onChange={e => patch({ ais: e.target.checked, rec: null, mark: null })} />
              <span>{t('imagery.ds.aisCheck')}</span>
            </label>
          )}

          {calendarFailed ? (
            <div className="text-[11px] text-buoy">{t('imagery.ds.error')}</div>
          ) : months.length === 0 && calendarQuery.isSuccess ? (
            <div className="text-[11px] text-mist">{t('imagery.ds.noMonths')}</div>
          ) : (
            <DatasetMonth
              month={calendarMonth}
              days={calendarQuery.data?.days ?? []}
              selected={st.day}
              onMonth={m => patch({ month: m })}
              onPick={d => patch({ day: d, scene: null, rec: null, mark: null, selected: null })}
            />
          )}

          {st.day == null ? (
            <div className="text-[11px] text-mist">{t('imagery.ds.pickDay')}</div>
          ) : dayError ? (
            <div className="text-[11px] text-buoy">{t('imagery.ds.error')}</div>
          ) : (
            <>
              <div className="text-[11px] text-mist">
                {t('imagery.ds.scenesOfDay', { n: sensorScenes.length })}
              </div>
              <DayScenes
                scenes={scenes}
                selected={sceneKeyOrNull}
                sensor={st.sensor}
                onSensor={s => patch({ sensor: s, scene: null, rec: null, mark: null })}
                set={st.set}
                setTotals={setTotals}
                onSet={chooseSet}
                onPick={pickScene}
                onStep={step}
                position={position}
                empty={emptyScenes}
                showEmpty={allScenes}
                onShowEmpty={setAllScenes}
                marksReady={dsDay?.marks_ready === true}
                sets={aisOn}
                countOf={s => markCounts.get(sceneKey(s)) ?? 0}
              />
              {aisOn && (
                <div className="text-[11px] text-mist">
                  {t('imagery.ds.ais', { n: dsDay?.ais.length ?? 0 })}
                </div>
              )}
            </>
          )}

          {scene && (
            <div className="space-y-1 border-t border-hull pt-2">
              {/* Площадь ОСМОТРА — то, без чего пустая карта врёт. */}
              <div className="text-[11px] text-mist">
                {inspected ? t('imagery.searched', { km2: Math.round(searchedKm2) }) : t('imagery.notSearched')}
              </div>
              {!inspected && box && (
                <button type="button" disabled={scanBusy}
                        onClick={() => scanStart.mutate(sceneKey(scene))}
                        className={`w-full rounded-xl border px-2 py-1 text-[12px] ${
                          scanBusy ? 'border-hull text-mist' : 'border-sonar text-foam hover:bg-hull/50'}`}>
                  {t('imagery.scan.scene')}
                </button>
              )}
              {scanOfScene && scan.busy && (
                <div className="h-1 overflow-hidden rounded bg-hull">
                  <div className="h-full bg-sonar transition-all" style={{ width: `${scan.percent}%` }} />
                </div>
              )}
              {scanOfScene && scan.line && (
                <div className={`text-[11px] leading-tight ${scan.failed ? 'text-buoy' : 'text-mist'}`}>
                  {t(scan.line.key, scan.line.values)}
                </div>
              )}
              <label className="flex items-center gap-2 text-[11px] text-mist">
                <span>{t('imagery.picture.opacity')}</span>
                <input type="range" min={0} max={1} step={0.05} value={opacity}
                       onChange={e => setOpacity(Number(e.target.value))} className="flex-1" />
              </label>
              {scene.sensor === 'optical' && (
                <label className="flex items-center gap-2 text-[11px] text-mist">
                  <input type="checkbox" checked={visual} onChange={() => setVisual(v => !v)} />
                  <span>{t('imagery.ds.visual')}</span>
                </label>
              )}
            </div>
          )}

          {/* Отметки осмотров: отбор по вердикту, он же легенда цветов. */}
          {Object.values(facets).some(n => n > 0) && (
            <div className="space-y-0.5 border-t border-hull pt-2">
              {Object.entries(facets).filter(([, n]) => n > 0).map(([verdict, count]) => (
                <button key={verdict} type="button"
                        onClick={() => patch({ verdicts: st.verdicts.includes(verdict)
                          ? st.verdicts.filter(v => v !== verdict) : [...st.verdicts, verdict] })}
                        className={`flex w-full items-center gap-2 rounded-lg px-1 py-0.5 text-left ${
                          st.verdicts.length === 0 || st.verdicts.includes(verdict) ? 'text-foam' : 'text-mist/60'}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: verdictColor(verdict) }} />
                  <span className="flex-1 truncate">{t(verdictLabelKey(verdict))}</span>
                  <span className="tabular-nums text-mist">{count}</span>
                </button>
              ))}
            </div>
          )}

          {aisOn && <DatasetLegend />}

          <div className="border-t border-hull pt-2">
            <label className="flex items-center gap-2 text-[11px] text-mist">
              <input type="checkbox" checked={st.looked} onChange={() => patch({ looked: !st.looked })} />
              <span>{t('imagery.showLooked')}</span>
            </label>
            <BoundaryToggles
              on={{ eez: st.eez, districts: st.fd }}
              onToggle={key => (key === 'eez' ? patch({ eez: !st.eez }) : patch({ fd: !st.fd }))}
              meta={boundaryMeta}
            />
          </div>

          {/* Атрибуция едет РЯДОМ с данными: CC BY-NC требует ссылки к показанному. */}
          {data?.ais.attribution && sweepMarks.features.length > 0 && (
            <div className="border-t border-hull pt-1 text-[10px] leading-tight text-mist/80">
              {data.ais.attribution}
            </div>
          )}
        </div>
      </MapPanel>

      {marksQuery.isError && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-xl border border-hull bg-abyss/95 px-3 py-1 text-[12px] text-mist shadow-console backdrop-blur">
          {t('imagery.error.marks')}
        </div>
      )}

      {(selectedSweepMark || dsMarkCard) && (
        <div className="pointer-events-none absolute bottom-3 right-3 z-10">
          <MarkCard data={(selectedSweepMark ?? dsMarkCard)!}
                    onClose={() => patch({ selected: null, mark: null })} />
        </div>
      )}

      {selectedRecord && (
        <RecordCard
          recordId={selectedRecord.record_id}
          anchor={{ lng: selectedRecord.lon, lat: selectedRecord.lat }}
          onClose={() => patch({ rec: null })}
        />
      )}
    </div>
  )
}

/** Слои раздела снизу вверх: фиксы АИС, контур снимка, рамки осмотра, луч
 *  следа, отметки осмотров, кольца записей, отметки без АИС. Картинка снимка
 *  кладётся позже — под фиксы АИС. */
function addScoutLayers(map: maplibregl.Map, on: {
  onSweepMark: (key: string) => void
  onRecord: (id: number) => void
  onDsMark: (id: number) => void
}) {
  for (const id of [DS_AIS_SOURCE, DS_FOOTPRINT_SOURCE, LOOKED_SOURCE, WAKE_SOURCE, MARKS_SOURCE,
    DS_RECORDS_SOURCE, DS_MARKS_SOURCE, DS_INSTRUMENT_SOURCE]) {
    if (!map.getSource(id)) map.addSource(id, { type: 'geojson', data: EMPTY_FC as never })
  }
  addUnderBoundaries(map, {
    id: DS_AIS_LAYER, type: 'circle', source: DS_AIS_SOURCE,
    paint: { 'circle-radius': 1.8, 'circle-color': '#94a3b8', 'circle-opacity': 0.75 },
  })
  addUnderBoundaries(map, {
    id: DS_FOOTPRINT_LAYER, type: 'line', source: DS_FOOTPRINT_SOURCE,
    paint: { 'line-color': '#38bdf8', 'line-width': 1.2, 'line-opacity': 0.7 },
  })
  // Рамки осмотра — фон, а не предмет.
  addUnderBoundaries(map, {
    id: LOOKED_FILL_LAYER, type: 'fill', source: LOOKED_SOURCE,
    paint: { 'fill-color': '#38bdf8', 'fill-opacity': 0.07 },
  })
  map.addLayer({
    id: LOOKED_LINE_LAYER, type: 'line', source: LOOKED_SOURCE,
    paint: { 'line-color': '#38bdf8', 'line-width': 1, 'line-opacity': 0.45 },
  })
  // Луч следа — ПРОБА фиксированной длины, отсюда пунктир; под точкой-ответом.
  map.addLayer({
    id: WAKE_LAYER, type: 'line', source: WAKE_SOURCE,
    layout: { 'line-cap': 'round' },
    paint: { 'line-color': '#7dd3fc', 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.75 },
  })
  map.addLayer({
    id: MARKS_LAYER, type: 'circle', source: MARKS_SOURCE,
    paint: {
      'circle-radius': 5,
      'circle-color': verdictColorExpression() as never,
      'circle-stroke-color': aisStrokeColorExpression() as never,
      'circle-stroke-width': aisStrokeWidthExpression() as never,
    },
  })
  map.addLayer({
    id: DS_RECORDS_HALO_LAYER, type: 'circle', source: DS_RECORDS_SOURCE,
    paint: {
      'circle-radius': ringRadiusExpression() as never,
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': '#0f172a',
      'circle-stroke-width': 4,
      'circle-stroke-opacity': 0.55,
    },
  })
  map.addLayer({
    id: DS_RECORDS_LAYER, type: 'circle', source: DS_RECORDS_SOURCE,
    paint: {
      'circle-radius': ringRadiusExpression() as never,
      'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': statusColorExpression() as never,
      'circle-stroke-width': 2,
    },
  })
  map.addLayer({
    id: DS_MARKS_LAYER, type: 'circle', source: DS_MARKS_SOURCE,
    paint: {
      'circle-radius': 5,
      'circle-color': MARK_COLOR,
      'circle-stroke-color': '#0f172a',
      'circle-stroke-width': 1.5,
    },
  })
  // Где прибор поставил судно выбранной записи: пунктир от позиции АИС и
  // перекрестье. Клика нет — это подпись к кольцу, а не самостоятельный предмет.
  map.addLayer({
    id: DS_INSTRUMENT_LINE_LAYER, type: 'line', source: DS_INSTRUMENT_SOURCE,
    filter: ['==', ['geometry-type'], 'LineString'],
    paint: { 'line-color': '#f8fafc', 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.9 },
  })
  map.addLayer({
    id: DS_INSTRUMENT_DOT_LAYER, type: 'circle', source: DS_INSTRUMENT_SOURCE,
    filter: ['==', ['geometry-type'], 'Point'],
    paint: {
      'circle-radius': 4, 'circle-color': 'rgba(0,0,0,0)',
      'circle-stroke-color': '#f8fafc', 'circle-stroke-width': 2,
    },
  })
  for (const layer of [MARKS_LAYER, DS_RECORDS_LAYER, DS_MARKS_LAYER]) {
    map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer' })
    map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = '' })
  }
  map.on('click', MARKS_LAYER, e => {
    const p = e.features?.[0]?.properties
    if (p) on.onSweepMark(markKey(Number(p.sweep_id), Number(p.local_id)))
  })
  map.on('click', DS_RECORDS_LAYER, e => {
    const id = Number(e.features?.[0]?.properties?.record_id)
    if (Number.isInteger(id)) on.onRecord(id)
  })
  map.on('click', DS_MARKS_LAYER, e => {
    const id = Number(e.features?.[0]?.properties?.mark_id)
    if (Number.isInteger(id)) on.onDsMark(id)
  })
}

/** Легенда сверки по смыслу: кольцо — судно по АИС, его цвет — что нашёл
 *  прибор; малиновая точка — судно, найденное прибором без пары в АИС. */
function DatasetLegend() {
  const t = useT()
  const ring = (status: string) => (
    <span key={status} className="inline-block h-2.5 w-2.5 rounded-full border-2"
          style={{ borderColor: statusColor(status) }} />
  )
  const rows: [ReactNode, string][] = [
    [<>{ring('identified')}{ring('found')}</>, 'imagery.ds.legend.match'],
    [ring('not_found'), 'imagery.ds.legend.missed'],
    [<span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: MARK_COLOR }} />,
     'imagery.ds.legend.noais'],
    [ring('new'), 'imagery.ds.legend.new'],
    [ring('excluded'), 'imagery.ds.legend.excluded'],
  ]
  return (
    <div className="space-y-0.5 border-t border-hull pt-2 text-[10px] text-mist">
      {rows.map(([swatch, key]) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className="flex w-6 shrink-0 items-center gap-0.5">{swatch}</span>
          <span>{t(key)}</span>
        </div>
      ))}
    </div>
  )
}

/** Все пары координат GeoJSON-геометрии (Polygon/MultiPolygon). */
function flatCoords(c: unknown): [number, number][] {
  if (Array.isArray(c) && typeof c[0] === 'number') return [c as [number, number]]
  return Array.isArray(c) ? c.flatMap(flatCoords) : []
}
